import * as React from 'react';
import { createPortal } from 'react-dom';

const DEFAULT_TYPE_DOT_COLOR = 'var(--color-icon-neutral-muted, currentColor)';

const normalizeHexColor = (color: string | null | undefined): string | undefined => {
  if (!color) return undefined;
  return color.startsWith('#') ? color : `#${color}`;
};

export interface DragLegendContent {
  /** Raw item type color (hex, with or without leading '#'), if known. */
  color: string | null | undefined;
  /** Item type name, or null/undefined to hide the type segment (e.g. multi-select). */
  typeLabel: string | null | undefined;
  /** Item name, or an already-translated summary like "3 items selected". */
  primaryLabel: string;
  /** Where the drag can go (already translated), or null to hide the segment entirely. */
  hint: string | null;
}

interface LegendSwatchProps {
  lineColor: string;
  label: string;
}

const LegendSwatch = ({ lineColor, label }: LegendSwatchProps) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
    <span style={{ display: 'inline-block', width: 14, height: 2, backgroundColor: lineColor }} />
    <span>{label}</span>
  </span>
);

export interface DragLegendPortalProps {
  /** Null hides the toolbar entirely (no drag in progress). */
  content: DragLegendContent | null;
  /** The grid's main element — the toolbar docks to its bottom-center. */
  containerEl: HTMLElement | null;
  validLabel: string;
  invalidLabel: string;
}

/**
 * A persistent legend shown for the whole drag gesture (not per-target): what's being
 * dragged, and a key for the valid/invalid indicator line colors. Portals into
 * document.body so it isn't clipped by the grid's own overflow/scroll container.
 */
export const DragLegendPortal = ({
  content,
  containerEl,
  validLabel,
  invalidLabel,
}: DragLegendPortalProps) => {
  const [position, setPosition] = React.useState<{ left: number; top: number } | null>(null);

  React.useLayoutEffect(() => {
    if (!content || !containerEl) {
      setPosition(null);
      return;
    }
    const rect = containerEl.getBoundingClientRect();
    setPosition({ left: rect.left + rect.width / 2, top: rect.bottom - 8 });
  }, [content, containerEl]);

  if (!content || !position) return null;

  return createPortal(
    <div
      style={{
        position: 'fixed',
        left: position.left,
        top: position.top,
        transform: 'translate(-50%, -100%)',
        pointerEvents: 'none',
        zIndex: 1400,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        padding: '12px 16px',
        borderRadius: 10,
        backgroundColor: 'var(--color-bg-neutral-bold)',
        color: 'var(--color-text-neutral-inverted)',
        fontSize: 12,
        lineHeight: '16px',
        whiteSpace: 'nowrap',
        boxShadow: '0 2px 8px rgba(0,0,0,0.32)',
      }}
    >
      <span
        style={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          flex: '0 0 auto',
          backgroundColor: normalizeHexColor(content.color) ?? DEFAULT_TYPE_DOT_COLOR,
        }}
      />
      {content.typeLabel && (
        <span
          style={{
            fontWeight: 700,
            letterSpacing: '0.02em',
            textTransform: 'uppercase',
            opacity: 0.7,
          }}
        >
          {content.typeLabel}
        </span>
      )}
      <span style={{ fontWeight: 600 }}>{content.primaryLabel}</span>
      {content.hint && (
        <>
          <span style={{ opacity: 0.4 }}>|</span>
          <span style={{ opacity: 0.85 }}>{content.hint}</span>
        </>
      )}
      <span style={{ opacity: 0.4 }}>|</span>
      <LegendSwatch lineColor="var(--color-bg-accent-blue)" label={validLabel} />
      <LegendSwatch lineColor="var(--color-border-error)" label={invalidLabel} />
    </div>,
    document.body,
  );
};
